/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { CONTRACT_RATE, ReferentPriceTypeOptions, ReferentPriceTypes } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { BidInput } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { Formik, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { getConvertBiosimilarErrorMessage } from 'services/treatmentTransitionHelpers';
import {
    useGetBiosimilarConversionProductsQuery,
    useGetEligibleSuppliersQuery,
} from 'store/api/referenceApi';
import {
    useConvertBiosimilarMutation,
    useGetIsTestClaimEnabledQuery,
    useRunTestClaimsForProductMutation,
} from 'store/api/treatmentApi';
import { fetchReferralDetails } from 'store/referrals/referralsSlice';

const Title = 'Convert to Biosimilar';

const ReasonOptions = [
    'Coverage exception',
    'Member preference',
    'Prescriber preference',
    'Pharmacy preference',
];

const MEDISPAN_PRICE_SOURCE = 'MEDISPAN';

const DestinationSupplierChoices = {
    REMAIN: 'REMAIN',
    SELECT_ANOTHER: 'SELECT_ANOTHER',
};

const DestinationSupplierOptions = [
    {
        value: DestinationSupplierChoices.REMAIN,
        text: 'Remain at this pharmacy',
    },
    {
        value: DestinationSupplierChoices.SELECT_ANOTHER,
        text: 'Select another pharmacy',
    },
];

const initialValues = {
    destinationProduct: undefined,
    rate: undefined,
    priceIsFrozen: false,
    destinationSupplierChoice: undefined,
    destinationSupplierNpi: undefined,
    useContractRate: false,
    ...ReasonInput.initialValues,
};

const buildDestinationRate = (values) => {
    if (values.useContractRate) {
        return {
            rate: CONTRACT_RATE,
            referent_price_type: ReferentPriceTypes.AWP,
            price_source: MEDISPAN_PRICE_SOURCE,
            price_is_frozen: false,
        };
    }
    return {
        rate: Decimal.div(values.rate, 100).toNumber(),
        referent_price_type: values.referentPriceType,
        price_is_frozen: values.priceIsFrozen,
        price_source: MEDISPAN_PRICE_SOURCE,
    };
};

const buildDestinationSupplierNpi = (values, referralDetails) => {
    if (values.destinationSupplierChoice === DestinationSupplierChoices.REMAIN) {
        return referralDetails?.supplierNpi;
    }
    if (values.destinationSupplierChoice === DestinationSupplierChoices.SELECT_ANOTHER) {
        return values.destinationSupplierNpi;
    }
    return null;
};

const isSaveDisabledByTestClaim = ({
    showConversionDialogTestClaimResult,
    testClaimEnabled,
    isRunningTestClaims,
    payableProductServiceId,
}) =>
    !!(
        showConversionDialogTestClaimResult &&
        testClaimEnabled &&
        (isRunningTestClaims || !payableProductServiceId)
    );

const buildTestClaimStatus = ({
    enabled,
    isRunningTestClaims,
    testClaimData,
    destinationProductValue,
}) => {
    if (!enabled) {
        return undefined;
    }

    if (isRunningTestClaims && destinationProductValue) {
        return (
            <span className="sds_convert-biosimilar-dialog__test-claim-status">
                <span className="sds_ellipsis-animation">Running test claims</span>
            </span>
        );
    }

    if (testClaimData === undefined || !destinationProductValue) {
        return undefined;
    }

    const isPayable = !!testClaimData.payableProductServiceId;

    return (
        <span className="sds_convert-biosimilar-dialog__test-claim-status">
            <InlineMessage
                intent={isPayable ? InlineMessage.Intent.SUCCESS : InlineMessage.Intent.ERROR}
                label={isPayable ? 'Payable' : 'Not payable'}
            />
        </span>
    );
};

const useDestinationEligibleSuppliers = ({
    referralDetails = undefined,
    memberDetails = undefined,
    destinationProduct = undefined,
}) => {
    const { payerId, segmentId, benefit, placeOfService } = referralDetails ?? {};

    return useGetEligibleSuppliersQuery(
        destinationProduct && memberDetails
            ? {
                  payerId,
                  segmentId,
                  memberState: memberDetails.memberState,
                  productServiceOid: destinationProduct.value,
                  benefit,
                  placeOfService,
                  isBrand: destinationProduct.is_brand,
                  lineOfBusiness: memberDetails.lineOfBusiness,
                  lobSegment: memberDetails.lobSegment,
              }
            : skipToken,
    );
};

const DestinationSupplierRadio = ({
    referralDetails = undefined,
    memberDetails = undefined,
    destinationProduct = undefined,
}) => {
    const formik = useFormikContext();
    const currentSupplierNpi = referralDetails?.supplierNpi;
    const { data: eligibleSuppliers, isFetching } = useDestinationEligibleSuppliers({
        referralDetails,
        memberDetails,
        destinationProduct,
    });

    const currentSupplierIsEligible = (eligibleSuppliers ?? [])
        .map(({ npi }) => npi)
        .includes(currentSupplierNpi);

    const isRemainEnabled = !destinationProduct || (!isFetching && currentSupplierIsEligible);
    const showIneligibleMessage = destinationProduct && !isFetching && !currentSupplierIsEligible;

    return (
        <>
            <RadioButtonGroupFormik
                label="Destination Supplier"
                required
                options={[
                    {
                        ...DestinationSupplierOptions[0],
                        disabled: !isRemainEnabled,
                    },
                    DestinationSupplierOptions[1],
                ]}
                name="destinationSupplierChoice"
                selectedValue={formik.values.destinationSupplierChoice}
                onChange={(newVal) => {
                    formik.setValues({
                        ...formik.values,
                        destinationSupplierChoice: newVal,
                        destinationSupplierNpi:
                            newVal === DestinationSupplierChoices.SELECT_ANOTHER
                                ? formik.values.destinationSupplierNpi
                                : undefined,
                    });
                }}
            />
            {showIneligibleMessage && (
                <div className="margin__bottom--medium">
                    <InlineMessage
                        intent={InlineMessage.Intent.WARNING}
                        label="Current pharmacy is unable to supply selected product."
                    />
                </div>
            )}
        </>
    );
};

DestinationSupplierRadio.propTypes = {
    referralDetails: PropTypes.shape({
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
        supplierNpi: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberState: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
    }),
    destinationProduct: PropTypes.shape({
        value: PropTypes.string,
        is_brand: PropTypes.bool,
    }),
};

const DestinationSupplierSelect = ({ referralDetails = undefined, memberDetails = undefined }) => {
    const formik = useFormikContext();
    const { destinationProduct, destinationSupplierChoice } = formik.values;
    const currentSupplierNpi = referralDetails?.supplierNpi;

    const { data: eligibleSuppliers, isFetching } = useDestinationEligibleSuppliers({
        referralDetails,
        memberDetails,
        destinationProduct,
    });

    const supplierOptions = (eligibleSuppliers ?? []).filter(
        ({ npi }) => npi !== currentSupplierNpi,
    );

    if (
        !destinationProduct ||
        destinationSupplierChoice !== DestinationSupplierChoices.SELECT_ANOTHER
    ) {
        return null;
    }

    return (
        <SuggestSingleSelectFormik
            id="destinationSupplierNpi"
            label="Pharmacy"
            required
            values={supplierOptions}
            isLoading={isFetching}
            selectedItem={supplierOptions.find(
                ({ npi }) => npi === formik.values.destinationSupplierNpi,
            )}
            onChange={(newValue) =>
                formik.setValues({
                    ...formik.values,
                    destinationSupplierNpi: newValue?.npi,
                    useContractRate: false,
                    priceIsFrozen: false,
                })
            }
        />
    );
};

DestinationSupplierSelect.propTypes = {
    referralDetails: PropTypes.shape({
        payerId: PropTypes.string,
        segmentId: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
        supplierNpi: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberState: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
    }),
};

export const ConvertBiosimilarDialogLegacy = ({
    isOpen = false,
    onClose,
    referralDetails = undefined,
    memberDetails = undefined,
}) => {
    const dispatch = useDispatch();
    const { showConversionDialogTestClaimResult } = useFlags();
    const { data: biosimilarConversionOptions, isFetching: biosimilarConversionOptionsIsFetching } =
        useGetBiosimilarConversionProductsQuery(
            referralDetails?.productServiceOid ? referralDetails.productServiceOid : skipToken,
        );
    const { data: testClaimEnabled, isFetching: testClaimEnabledIsFetching } =
        useGetIsTestClaimEnabledQuery(
            referralDetails?.payerId && referralDetails?.segmentId
                ? { payerId: referralDetails.payerId, segmentId: referralDetails.segmentId }
                : skipToken,
        );
    const [convertBiosimilar] = useConvertBiosimilarMutation();
    const [
        runTestClaimsForProduct,
        { isLoading: isRunningTestClaims, data: testClaimData, reset: resetTestClaims },
    ] = useRunTestClaimsForProductMutation();

    const validationSchema = Yup.object({
        destinationProduct: Yup.object({
            name: Yup.string(),
            value: Yup.string(),
            is_brand: Yup.bool(),
        }).required(validationStrings.requiredField.errorMessage),
        destinationSupplierChoice: Yup.string()
            .oneOf(Object.values(DestinationSupplierChoices))
            .required(validationStrings.requiredField.errorMessage),
        destinationSupplierNpi: Yup.string().when('destinationSupplierChoice', {
            is: DestinationSupplierChoices.SELECT_ANOTHER,
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        referentPriceType: Yup.string().when('useContractRate', {
            is: false,
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        rate: BidInput.validator().when('useContractRate', {
            is: false,
            then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
        }),
        useContractRate: Yup.bool(),
        priceIsFrozen: Yup.bool().required(validationStrings.requiredField.errorMessage),
        ...ReasonInput.validationSchema,
    });

    const handleClose = (resetForm) => {
        resetTestClaims();
        resetForm();
        onClose();
    };

    const shouldShowTestClaimStatus = showConversionDialogTestClaimResult && !!testClaimEnabled;

    const handleDestinationProductChange = async (product, formik) => {
        formik.setValues({
            ...formik.values,
            destinationProduct: product,
            destinationSupplierChoice: undefined,
            destinationSupplierNpi: undefined,
            useContractRate: false,
            priceIsFrozen: false,
        });

        if (!product || !referralDetails || !memberDetails || !shouldShowTestClaimStatus) {
            resetTestClaims();
            return;
        }

        try {
            // No destination pharmacy is chosen here, so treatment bills its claim check NPI.
            await runTestClaimsForProduct({
                payerId: referralDetails.payerId,
                segmentId: referralDetails.segmentId,
                memberId: memberDetails.memberId,
                patientFirstName: memberDetails.firstName,
                patientLastName: memberDetails.lastName,
                patientDob: memberDetails.dateOfBirth,
                prescriberNpi: referralDetails.prescriberNpi,
                productServiceIds: product.productServiceIds ?? [],
                asOfDate: formatDate(dayjs(), DateFormats.HYPHEN_DATE),
            }).unwrap();
        } catch (err) {
            handleError('Unable to run test claims. Please try again.', err);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            validateOnMount
            onSubmit={async (values, { resetForm }) => {
                if (!referralDetails) {
                    return;
                }
                const { payerId, treatmentId, referralId } = referralDetails;
                try {
                    await convertBiosimilar({
                        payerId,
                        treatmentId,
                        body: {
                            destination_product_service_oid: values.destinationProduct.value,
                            ...(testClaimData?.payableProductServiceId && {
                                destination_product_service_id:
                                    testClaimData.payableProductServiceId,
                            }),
                            destination_supplier_npi: buildDestinationSupplierNpi(
                                values,
                                referralDetails,
                            ),
                            destination_rate: buildDestinationRate(values),
                            destination_is_brand: values.destinationProduct.is_brand,
                            ...ReasonInput.submitValues(values),
                        },
                    }).unwrap();
                    dispatch(fetchReferralDetails({ payerId, referralId }));
                    SuccessToast({ message: 'Biosimilar conversion initiated successfully.' });
                    handleClose(resetForm);
                } catch (err) {
                    handleError(getConvertBiosimilarErrorMessage(err), err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle={Title}
                    width={560}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) ||
                        formik.isSubmitting ||
                        isSaveDisabledByTestClaim({
                            showConversionDialogTestClaimResult,
                            testClaimEnabled,
                            isRunningTestClaims,
                            payableProductServiceId: testClaimData?.payableProductServiceId,
                        })
                    }
                    onSecondaryButtonClick={() => handleClose(formik.resetForm)}
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="destinationProduct"
                                label="Product"
                                required
                                disableAutoPopulate
                                values={biosimilarConversionOptions}
                                isLoading={
                                    biosimilarConversionOptionsIsFetching ||
                                    testClaimEnabledIsFetching
                                }
                                selectedItem={formik.values.destinationProduct}
                                itemsEqual={(a, b) =>
                                    a?.value === b?.value && a?.is_brand === b?.is_brand
                                }
                                onChange={(product) =>
                                    handleDestinationProductChange(product, formik)
                                }
                                helperText={buildTestClaimStatus({
                                    enabled: shouldShowTestClaimStatus,
                                    isRunningTestClaims,
                                    testClaimData,
                                    destinationProductValue:
                                        formik.values.destinationProduct?.value,
                                })}
                            />
                            {formik.values.destinationProduct && (
                                <>
                                    <DestinationSupplierRadio
                                        referralDetails={referralDetails}
                                        memberDetails={memberDetails}
                                        destinationProduct={formik.values.destinationProduct}
                                    />
                                    <DestinationSupplierSelect
                                        referralDetails={referralDetails}
                                        memberDetails={memberDetails}
                                    />
                                    {!formik.values.useContractRate && (
                                        <>
                                            <RadioButtonGroupFormik
                                                name="referentPriceType"
                                                label="Referent Price Type"
                                                required
                                                options={ReferentPriceTypeOptions}
                                                onChange={(newValue) => {
                                                    formik.setFieldValue(
                                                        'referentPriceType',
                                                        newValue,
                                                    );
                                                    formik.setFieldValue('rate', undefined);
                                                }}
                                            />
                                            <BidInput
                                                id="rate"
                                                label="Rate"
                                                required
                                                referentPriceType={formik.values.referentPriceType}
                                            />
                                        </>
                                    )}
                                    <Checkbox
                                        id="useContractRate"
                                        label="Use Contract Rate"
                                        onChange={(checked) => {
                                            formik.setFieldValue('useContractRate', checked);
                                            formik.setFieldValue('referentPriceType', undefined);
                                            formik.setFieldValue('rate', undefined);
                                        }}
                                        checked={formik.values.useContractRate}
                                    />
                                    <Checkbox
                                        name="priceIsFrozen"
                                        label="Lock unit price for the life of assigned referrals."
                                        checked={formik.values.priceIsFrozen}
                                        onChange={(checked) =>
                                            formik.setFieldValue('priceIsFrozen', checked)
                                        }
                                    />
                                    <ReasonInput reasons={ReasonOptions} />
                                    <InlineMessage
                                        intent={InlineMessage.Intent.WARNING}
                                        label="The current referral will be expired and a new one will be created for acceptance."
                                        wrapText
                                    />
                                </>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ConvertBiosimilarDialogLegacy.Title = Title;

ConvertBiosimilarDialogLegacy.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    referralDetails: PropTypes.shape({
        referralId: PropTypes.string.isRequired,
        payerId: PropTypes.string.isRequired,
        treatmentId: PropTypes.string.isRequired,
        segmentId: PropTypes.string,
        productServiceOid: PropTypes.string,
        prescriberNpi: PropTypes.string,
        supplierNpi: PropTypes.string,
        benefit: PropTypes.string,
        placeOfService: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        memberState: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
        dateOfBirth: PropTypes.string,
    }),
};
